import { type z } from 'zod';

import { type EndpointConfig, type Methods, type StatusCode } from './types';

const DATADOG_CHAR_LIMIT = 220000;

export class ClientValidationError<
    Method extends Methods,
    Path extends string,
    Query extends z.ZodObject | undefined,
    Headers extends z.ZodObject | undefined,
    Body extends z.ZodType | undefined,
    Responses extends Partial<Record<StatusCode, z.ZodType>>,
> extends Error {
    static readonly errorName = 'API client error';

    constructor(
        public type: 'Body' | 'Query' | 'Headers' | 'Response',
        // We want to match any config, regardless of its generics

        public config: EndpointConfig<Method, Path, Query, Headers, Body, Responses>,
        public cause: z.ZodError,
        public initialData?: unknown,
    ) {
        super(`validation failed`);
        this.name = ClientValidationError.errorName;
        if (import.meta.env.MODE !== 'test') {
            this.logError();
        }
    }

    private logError() {
        const { method, path } = this.config;

        // Keep the alignment of the template string as-is
        const message = `[fetcher] ${this.type} validation failed:
Method: ${method}
URL: ${path}
Error: ${this.cause.message}

Data provided:
${this.initialData ? JSON.stringify(this.initialData, null, 2) : 'None'}
        `;

        if (import.meta.env.MODE === 'production') {
            console.error(message.substring(0, DATADOG_CHAR_LIMIT));
        } else {
            console.error(message);
        }
    }
}
